import { sendCampaignLaunchNotification } from './slack-wrapper'
import { supabase } from './supabase'

/**
 * Manually trigger a campaign launch notification for testing
 */
export async function triggerCampaignLaunchNotification(campaignId: string): Promise<{ success: boolean; message: string }> {
  try {
    console.log('🧪 Manually triggering campaign launch notification for:', campaignId)

    // Fetch campaign data
    const { data: campaign, error } = await supabase
      .from('campaigns')
      .select(`
        *,
        clients!inner(name)
      `)
      .eq('id', campaignId)
      .single()

    if (error) {
      console.error('Error fetching campaign:', error)
      return { success: false, message: `Campaign not found: ${error.message}` }
    }

    if (!campaign) {
      return { success: false, message: 'Campaign not found' }
    }

    // Get client Slack channel
    const { data: client } = await supabase
      .from('clients')
      .select('slack_main_channel')
      .eq('id', campaign.client_id)
      .single()

    // Prepare campaign data
    const campaignData = {
      id: campaign.id,
      name: campaign.name,
      client_id: campaign.client_id,
      client_name: campaign.clients?.name,
      status: campaign.status,
      campaign_type: campaign.campaign_type,
      version: campaign.version,
      target_regions: campaign.target_regions,
      channels: campaign.channels,
      description: campaign.description,
      total_leads: campaign.total_leads,
      created_at: campaign.created_at
    }

    // Send notification
    const success = await sendCampaignLaunchNotification(campaignData, client?.slack_main_channel)

    if (success) {
      return { 
        success: true, 
        message: `Campaign launch notification sent successfully for "${campaign.name}"` 
      }
    } else {
      return { 
        success: false, 
        message: 'Failed to send campaign launch notification' 
      }
    }
  } catch (error) {
    console.error('Error triggering campaign launch notification:', error)
    return { 
      success: false, 
      message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` 
    }
  }
}

/**
 * Test the campaign launch notification system with mock data
 */
export async function testCampaignLaunchNotification(): Promise<{ success: boolean; message: string }> {
  try {
    console.log('🧪 Testing campaign launch notification with mock data')

    const mockCampaign = {
      id: 'test-campaign-' + Date.now(),
      name: 'Test Campaign Launch',
      client_id: 'test-client',
      client_name: 'Test Client',
      status: 'active',
      campaign_type: 'bulk',
      version: '1.0',
      target_regions: ['North America', 'Europe'],
      channels: ['email', 'linkedin'],
      description: 'This is a test campaign launch notification',
      total_leads: 1000,
      created_at: new Date().toISOString()
    }

    const success = await sendCampaignLaunchNotification(mockCampaign, '#test-channel')

    if (success) {
      return { 
        success: true, 
        message: 'Test campaign launch notification sent successfully' 
      }
    } else {
      return { 
        success: false, 
        message: 'Failed to send test campaign launch notification' 
      }
    }
  } catch (error) {
    console.error('Error testing campaign launch notification:', error)
    return { 
      success: false, 
      message: `Test error: ${error instanceof Error ? error.message : 'Unknown error'}` 
    }
  }
}
